import { Button, Box } from "@material-ui/core"
import Dialog, { DialogProps } from "@material-ui/core/Dialog"
import { default as DialogActions, default as DialogContent } from "@material-ui/core/DialogContent"
import DialogTitle from "@material-ui/core/DialogTitle"
import React, { FC, useEffect, useMemo, useState } from "react"
import { useDispatch } from 'react-redux'
import updateClass from '../../../actions/class/classUpdate'
import Checkbox from "../../../components/Checkbox"
import KeyboardTimePicker from "../../../components/KeyboardTimePicker"
import { KeyboardDatePicker, KeyboardTimePicker as MaterialKeyboardTimePicker } from '@material-ui/pickers';
import Selector from "../../../components/Selector"
import TextField from "../../../components/TextField"
import Class, { activityStates, activityStateToDescription, cancellationReasonDescription, CommRoute, commRoutes, commRouteToDescription, phases, phaseToDescription, statusToDescription } from '../../../models/Class'
import Group from "../../../models/Group"
import School from "../../../models/School"
import { isValidSchoolTime, dateToString } from '../../../utils/days';
import { sortAlphabetically, sortByAdapter } from "../../../utils/StringUtils"
import useStyles from "./styles"
import AssignationSummary from "../../components/AssignationSummary";
import { DialogBasicProps } from "../../components/Utils/Dialogs/AnahiDialogIconButton";
import { AppProp, connectApp } from "../../../reducers"
import { Platform } from "../../../models/Platform"
import { equals, find } from "ramda"
import { Proposal } from "../../../models/Proposal"
import { AccessTime } from "@material-ui/icons"
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"
import { isAdmin } from '../../../reducers/auth/session'

type ClassReportProps = DialogBasicProps & {
  selectedClass: Class,
  disabled: boolean
} & AppProp<'platformList' | 'proposalList' | 'session'>

const preventBackdropClick = (onClose: () => void): DialogProps['onClose'] => (_event, reason) => {
  if (reason !== 'backdropClick') {
    onClose()
  }
}

const extractGoogleDriveId = (input: string): string | null => {
  if (!input?.trim()) return null

  let url: URL
  try {
    url = new URL(input.trim())
  } catch {
    return null
  }

  const host = url.hostname.toLowerCase()
  const isGoogleDriveHost =
    host === 'drive.google.com' ||
    host === 'docs.google.com'

  if (!isGoogleDriveHost) return null

  let match = url.pathname.match(/\/file\/d\/([a-zA-Z0-9_-]{10,})/)
  if (match?.[1]) return match[1]

  match = url.pathname.match(/\/(document|spreadsheets|presentation)\/d\/([a-zA-Z0-9_-]{10,})/)
  if (match?.[2]) return match[2]

  match = url.pathname.match(/\/drive\/folders\/([a-zA-Z0-9_-]{10,})/)
  if (match?.[1]) return match[1]

  const idParam = url.searchParams.get('id')
  if (idParam && /^[a-zA-Z0-9_-]{10,}$/.test(idParam)) return idParam

  return null
}

const isValidGoogleDriveUrl = (input: string): boolean => {
  const id = extractGoogleDriveId(input)
  return !!id && /^[a-zA-Z0-9_-]{10,}$/.test(id)
}

const ClassReportDialog: FC<ClassReportProps> = ({
  onClose,
  open,
  selectedClass,
  disabled,
  platformList,
  proposalList,
  session
}) => {
  const stylesClasses = useStyles()
  const dispatch = useDispatch()

  const [updatedClass, setUpdatedClass] = useState<Class>(new Class(selectedClass))
  const [isTimeConfirmed, setIsTimeConfirmed] = useState<boolean>(updatedClass.isDictated())
  const [newCoordDateTime, setNewCoordDateTime] = useState<boolean>(true)
  const [displayedCommRoute, setDisplayedCommRoute] = useState<CommRoute | null>(null)

  const setClass = (key: keyof Class) => (event: any) => {
    setUpdatedClassValues({ [key]: event.target.value })
  }

  const updateReferencedValue = (valueName: string) => (event: any) => {
    const updatedValue = { [valueName]: event.target.value, [valueName + 'Id']: event.target.value.id }
    setUpdatedClassValues(updatedValue)
  }

  const setUpdatedClassValues = (values: Partial<Class>) => {
    setUpdatedClass(new Class({ ...updatedClass, ...values }))
  }

  /*
    This is necessary because to validate that the initial value is a valid menu item Material UI uses reference equality to check if the initial
    value is in the menu items list. If we dont do this there would not be an initial value in the fields in which this is used.
  */
  const getValueFromReference = <T extends unknown>(valueToFind: T | undefined, values: T[]): T | undefined => find(equals(valueToFind))(values)

  const getPlatformReference = (platform?: Platform): Platform | undefined => getValueFromReference(platform, platformList.platforms || [])
  const getProposalReference = (proposal?: Proposal): Proposal | undefined => getValueFromReference(proposal, proposalList.proposals || [])

  useEffect(() => {
    setUpdatedClass(selectedClass)
    setNewCoordDateTime((!selectedClass.coordDateTime && open))

    if (selectedClass.commRoute && open) {
      setDisplayedCommRoute(selectedClass.commRoute as CommRoute)
    } else {
      setDisplayedCommRoute(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedClass, open])

  const cancellationReasons = (): string[] =>
    sortAlphabetically(updatedClass.possibleCancellationReasons(), cancellationReasonDescription)

  const group: Group = selectedClass.assignation.group
  const school: School = group.school

  const coordinationWasDone = !!updatedClass.coordinationWasDone
  const coordinationLinkIsValid =
    !coordinationWasDone ||
    (!!updatedClass.coordScreenshot?.trim() && isValidGoogleDriveUrl(updatedClass.coordScreenshot))

  const coordinationSectionIsValid = useMemo(() => {
    if (!coordinationWasDone) return true

    return !!updatedClass.coordDateTime &&
      !!updatedClass.agreements?.trim() &&
      !!updatedClass.commRoute &&
      !!updatedClass.coordScreenshot?.trim() &&
      coordinationLinkIsValid
  }, [
    coordinationWasDone,
    updatedClass.coordDateTime,
    updatedClass.agreements,
    updatedClass.commRoute,
    updatedClass.coordScreenshot,
    coordinationLinkIsValid
  ])

  const disableAccept =
    !updatedClass.isValid() ||
    (updatedClass.needsTimeConfirmation() && !isTimeConfirmed) ||
    !coordinationSectionIsValid

  return (
    <Dialog fullWidth scroll="body" maxWidth="sm" open={open} onClose={preventBackdropClick(onClose)}>

      <DialogTitle>Informe de Clase del {selectedClass.prettyDateTime()}</DialogTitle>

      <DialogContent dividers>
        <AssignationSummary
          department={school.department}
          grade={group.grade}
          schoolNumber={school.number}
          classroomTeacher={group.teacher}
        />

        <Selector
          label="Estado de la clase"
          disabled={disabled}
          menuItems={selectedClass.possibleStatuses()}
          onChange={(event) => {
            const newStatus = event.target.value

            const nextClass = new Class({
              ...updatedClass,
              status: newStatus
            })

            const becomesNotDictated = !nextClass.isDictated()

            if (becomesNotDictated) {
              setUpdatedClass(new Class({
                ...updatedClass,
                status: newStatus,
                coordinationWasDone: false,
                coordinationInvalid: false,
                coordDateTime: null,
                agreements: '',
                commRoute: null,
                coordScreenshot: ''
              }))
              setDisplayedCommRoute(null)
              setNewCoordDateTime(true)
            } else {
              setUpdatedClass(new Class({
                ...updatedClass,
                status: newStatus
              }))
            }
          }}
          required={true}
          textAdapter={statusToDescription}
          value={updatedClass.status}
          variant="filled"
        />

        <KeyboardTimePicker
          label="Horario exacto en el cual inició la clase"
          date={updatedClass.sinceDateMoment()}
          disabled={disabled}
          hidden={updatedClass.isCancelled()}
          onChange={(date) => {
            if (date && date.isValid()) {
              updatedClass.sinceDate = dateToString(date.clone().dayOfYear(selectedClass.sinceDateMoment().dayOfYear()).year(selectedClass.sinceDateMoment().year()))
              setUpdatedClass(new Class(updatedClass))
              setIsTimeConfirmed(true)
            }
          }}
          required={true}
          validate={(date) => !!date && isValidSchoolTime(date)}
        />

        <Checkbox
          label="Confirmo que esa fue la hora a la que sucedió la clase"
          disabled={disabled}
          hidden={!updatedClass.isDictated()}
          onChange={(_event, checked) => setIsTimeConfirmed(checked)}
          required={true}
          value={isTimeConfirmed}
        />

        <Selector
          label="Plataforma utilizada"
          disabled={disabled}
          hidden={!updatedClass.isDictated()}
          menuItems={sortByAdapter(platformList.platforms || [], (platform) => platform.description)}
          onChange={updateReferencedValue('platform')}
          required={true}
          textAdapter={(platform: Platform) => platform.description}
          value={getPlatformReference(updatedClass.platform)}
          variant="filled"
        />

        <TextField
          label="Link a la captura de pantalla"
          disabled={disabled}
          hidden={!updatedClass.requiresScreenshot()}
          variant="filled"
          multiline={true}
          onChange={setClass('screenshot')}
          required={updatedClass.requiresScreenshot()}
          rows={1}
          value={updatedClass.screenshot}
        />

        <Selector
          label="Motivo"
          disabled={disabled}
          hidden={!updatedClass.isCancelled()}
          menuItems={cancellationReasons()}
          onChange={setClass('cancellationReason')}
          required={true}
          textAdapter={cancellationReasonDescription}
          value={updatedClass.cancellationReason}
          variant="filled"
        />

        <TextField
          label="Link de evidencia"
          disabled={disabled}
          hidden={!updatedClass.requiresEvidence()}
          variant="filled"
          multiline={true}
          onChange={setClass('screenshot')}
          required={updatedClass.requiresEvidence()}
          rows={1}
          value={updatedClass.screenshot}
        />

        <Checkbox
          label="¿Hubo cortes o problemas técnicos durante la VC?"
          disabled={disabled}
          hidden={!updatedClass.isDictated()}
          onChange={(_event, checked) => {
            updatedClass.hadIssuesDuringTheCall = checked
            setUpdatedClass(new Class(updatedClass))
          }}
          required={true}
          value={updatedClass.hadIssuesDuringTheCall}
        />

        <TextField
          label="Notas Técnicas"
          disabled={disabled}
          helperText="Si hubo problemas técnicos durante la VC (cortes, problemas de audio o imagen, etc.), detalle cómo fueron."
          hidden={!(updatedClass.isDictated() || updatedClass.requiresTechNotes())}
          variant="filled"
          multiline={true}
          onChange={setClass('technicalNotes')}
          required={updatedClass.requiresTechNotes()}
          rows={5}
          value={updatedClass.technicalNotes}
        />

        <Selector
          label="Propuesta"
          disabled={disabled}
          hidden={!updatedClass.isDictated()}
          menuItems={sortByAdapter(proposalList.proposals || [], (proposal) => proposal.description)}
          onChange={updateReferencedValue('proposal')}
          required={true}
          textAdapter={(proposal: Proposal) => proposal.description}
          value={getProposalReference(updatedClass.proposal)}
          variant="filled"
        />

        <Selector
          label="Etapa"
          disabled={disabled}
          hidden={!updatedClass.isDictated()}
          menuItems={phases}
          onChange={setClass("phase")}
          required={true}
          textAdapter={phaseToDescription}
          value={updatedClass.phase}
          variant="filled"
        />

        <Selector
          label="¿Se hizo la actividad de aula prevista?"
          disabled={disabled}
          hidden={!updatedClass.isDictated()}
          menuItems={activityStates}
          onChange={setClass('activityWasConcluded')}
          required={true}
          textAdapter={activityStateToDescription}
          value={updatedClass.activityWasConcluded}
          variant="filled"
        />

        <TextField
          label="Notas Pedagógicas"
          disabled={disabled}
          helperText="Desarrollo, detalles y recordatorios pedagógicos de los temas trabajados en clase."
          hidden={!updatedClass.isDictated()}
          variant="filled"
          multiline={true}
          onChange={setClass('pedagogicalNotes')}
          required={updatedClass.isDictated()}
          rows={5}
          value={updatedClass.pedagogicalNotes}
        />

        <TextField
          label="Notas Complementarias"
          disabled={disabled}
          helperText="Situaciones complementarias o paralelas al desarrollo pedagógico de la clase. Por ejemplo: La DA se ausentó en la mitad, un niñx tiene inconvenientes personales, hubo una pelea entre lxs niñxs, algunas computadoras no andan, etc."
          variant="filled"
          multiline={true}
          onChange={setClass('complementaryNotes')}
          rows={5}
          value={updatedClass.complementaryNotes}
        />

        <Checkbox
          label="Confirmo que realicé la coordinación con DA"
          disabled={disabled}
          hidden={!updatedClass.isDictated()}
          onChange={(_event, checked) => {
            setUpdatedClassValues({
              coordinationWasDone: checked,
              ...(checked ? {} : {
                coordDateTime: null,
                agreements: '',
                commRoute: null,
                coordScreenshot: ''
              })
            })

            if (!checked) {
              setDisplayedCommRoute(null)
              setNewCoordDateTime(true)
            }
          }}
          value={coordinationWasDone}
        />

        {updatedClass.isDictated() && coordinationWasDone && (
          <>
            <Box flexDirection="row" display="flex" justifyContent="space-between" alignItems="center">
              <KeyboardDatePicker
                label="Fecha de coordinación con DA"
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                margin="normal"
                format="DD/MM/YYYY"
                value={updatedClass.coordDateTime}
                onChange={(newDate: MaterialUiPickersDate) => {
                  if (newDate && newDate.isValid()) {
                    if (newCoordDateTime) {
                      setUpdatedClassValues({
                        coordDateTime: newDate.toDate().toISOString(),
                        createdAt: new Date().toISOString()
                      })
                    } else {
                      setUpdatedClassValues({
                        coordDateTime: newDate.toDate().toISOString(),
                        updatedAt: new Date().toISOString()
                      })
                    }
                  }
                }}
                disableToolbar
                required={true}
                KeyboardButtonProps={{ 'aria-label': 'Fecha de coordinación' }}
              />

              <MaterialKeyboardTimePicker
                label="Hora de coordinación con DA"
                margin="normal"
                required={true}
                InputLabelProps={{ shrink: true }}
                disabled={disabled}
                format="HH:mm"
                value={newCoordDateTime ? null : updatedClass.coordDateTime}
                ampm={false}
                onChange={(newTime: MaterialUiPickersDate) => {
                  if (newTime && newTime.isValid()) {
                    let updatedCoordDateTime: Date
                    if (updatedClass.coordDateTime) {
                      const formattedDate = updatedClass.coordDateTime.split('T')[0]
                      const newTimeWithDate = new Date(`${formattedDate}T${newTime.format('HH:mm')}:00`)
                      updatedCoordDateTime = newTimeWithDate
                    } else {
                      updatedCoordDateTime = new Date(`1970-01-01T${newTime.format('HH:mm')}:00`)
                    }

                    if (newCoordDateTime) {
                      setUpdatedClassValues({
                        coordDateTime: updatedCoordDateTime.toISOString(),
                        createdAt: new Date().toISOString()
                      })
                    } else {
                      setUpdatedClassValues({
                        coordDateTime: updatedCoordDateTime.toISOString(),
                        updatedAt: new Date().toISOString()
                      })
                    }
                    setNewCoordDateTime(false)
                  }
                }}
                KeyboardButtonProps={{ 'aria-label': 'Hora de coordinación' }}
                keyboardIcon={<AccessTime />}
              />
            </Box>

            <TextField
              label="Coordinación con DA"
              disabled={disabled}
              variant="filled"
              multiline={true}
              onChange={setClass('agreements')}
              required={true}
              rows={5}
              value={updatedClass.agreements}
              helperText="Ingresa un comentario sobre la coordinación, teniendo en cuenta los temas abordados, comentarios relevantes y compromisos sugeridos."
            />

            <Selector
              label="Vía de comunicación"
              disabled={disabled}
              required={true}
              menuItems={commRoutes}
              onChange={(event) => {
                const newValue = event.target.value
                setDisplayedCommRoute(newValue)
                setUpdatedClassValues({ commRoute: newValue })
              }}
              textAdapter={commRouteToDescription}
              value={displayedCommRoute}
              variant="filled"
            />

            <TextField
              label="Link de evidencia de coordinación con DA"
              disabled={disabled}
              variant="filled"
              multiline={true}
              onChange={setClass('coordScreenshot')}
              required={true}
              rows={1}
              value={updatedClass.coordScreenshot}
              error={!!updatedClass.coordScreenshot && !coordinationLinkIsValid}
              helperText={
                !!updatedClass.coordScreenshot && !coordinationLinkIsValid
                  ? "Ingresá un link válido de Google Drive o Google Docs."
                  : "Pegá un link válido de Google Drive / Google Docs donde esté la evidencia de coordinación."
              }
            />
          </>
        )}

        {isAdmin(session) && (
          <Checkbox
            label="Coordinación no válida"
            disabled={disabled}
            hidden={!(updatedClass.isDictated() && coordinationWasDone)}
            onChange={(_event, checked) => {
              setUpdatedClassValues({ coordinationInvalid: checked })
            }}
            value={!!updatedClass.coordinationInvalid}
          />
        )}
      </DialogContent>

      <DialogActions>
        {!disabled &&
          <Button
            className={stylesClasses.button}
            variant="contained"
            color="primary"
            size="small"
            type="submit"
            disabled={disableAccept}
            onClick={() => { onClose(); dispatch(updateClass(updatedClass)) }}>
            Aceptar
          </Button>}

        <Button
          className={stylesClasses.button}
          variant="contained"
          color="primary"
          size="small"
          onClick={onClose}>
          {disabled ? "Cerrar" : "Cancelar"}
        </Button>
      </DialogActions>

    </Dialog>
  )
}

export default connectApp('platformList', 'proposalList', 'session')(ClassReportDialog)